import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, CircularProgress, IconButton,
  Stack
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { uploadInvestment } from '../../actions/investments';
import { editInversion } from '../../actions/users';

const InvoiceUploadModal = ({ open, handleClose, item }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [xmlFile, setXmlFile] = useState(null);
  const [fechaFacturacion, setFechaFacturacion] = useState(null);
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (!open) {
      setPdfFile(null);
      setXmlFile(null);
      setFechaFacturacion(null);
      setError('');
    } else {
      // Al editar, precargar la fecha de facturación existente
      setFechaFacturacion(item?.fecha_facturacion ? dayjs(item.fecha_facturacion) : null);
    }
  }, [open, item]);

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (type === 'pdf' && file.type !== 'application/pdf') {
        setError('El archivo debe ser un PDF');
        return;
      }
      if (type === 'xml' && file.type !== 'text/xml' && !file.name.endsWith('.xml')) {
        setError('El archivo debe ser un XML');
        return;
      }
      setError('');
      if (type === 'pdf') setPdfFile(file);
      else setXmlFile(file);
    }
  };

  const handleUpload = async () => {
    if (!pdfFile || !xmlFile) {
      setError('Por favor selecciona ambos archivos (PDF y XML)');
      return;
    }
    if (!fechaFacturacion) {
      setError('Por favor selecciona la fecha de facturación');
      return;
    }

    setLoading(true);
    try {
      // Upload PDF
      const pdfName = pdfFile.name.split('.').slice(0, -1).join('.');
      const pdfFormData = new FormData();
      pdfFormData.append('file', pdfFile);
      pdfFormData.append('carpeta', `inversiones_facturas/${item.usrId}`);
      pdfFormData.append('nombre', pdfName);

      const pdfRes = await dispatch(uploadInvestment(pdfFormData));

      // Upload XML
      const xmlName = xmlFile.name.split('.').slice(0, -1).join('.');
      const xmlFormData = new FormData();
      xmlFormData.append('file', xmlFile);
      xmlFormData.append('carpeta', `inversiones_facturas/${item.usrId}`);
      xmlFormData.append('nombre', xmlName);

      const xmlRes = await dispatch(uploadInvestment(xmlFormData));

      if (pdfRes && xmlRes) {
        const updateData = {
          status: item.status,
          monto: item.monto,
          desc: item.desc,
          factura_pdf: pdfRes.secure_url,
          factura_xml: xmlRes.secure_url,
          fecha_facturacion: moment(fechaFacturacion.$d).toISOString()
        };

        await dispatch(editInversion(updateData, item.usrId, item.id));
        handleClose();
      } else {
        setError('Error al subir los archivos a la nube');
      }
    } catch (err) {
      console.error(err);
      setError('Hubo un error al procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {(item?.factura_pdf || item?.factura_xml) ? 'Editar Factura' : 'Subir Factura'}
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ py: 2 }}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>Fecha de facturación</Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                <DatePicker
                  label="Fecha de facturación"
                  value={fechaFacturacion}
                  onChange={(newValue) => setFechaFacturacion(newValue)}
                  format="DD-MM-YYYY"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>Archivo PDF</Typography>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={pdfFile ? <CheckCircleIcon color="success" /> : <UploadFileIcon />}
                sx={{ height: '56px', borderStyle: 'dashed' }}
              >
                {pdfFile ? pdfFile.name : 'Seleccionar PDF'}
                <input type="file" hidden accept=".pdf" onChange={(e) => handleFileChange(e, 'pdf')} />
              </Button>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>Archivo XML</Typography>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={xmlFile ? <CheckCircleIcon color="success" /> : <UploadFileIcon />}
                sx={{ height: '56px', borderStyle: 'dashed' }}
              >
                {xmlFile ? xmlFile.name : 'Seleccionar XML'}
                <input type="file" hidden accept=".xml" onChange={(e) => handleFileChange(e, 'xml')} />
              </Button>
            </Box>

            {error && (
              <Typography color="error" variant="caption">
                {error}
              </Typography>
            )}
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={loading || !pdfFile || !xmlFile || !fechaFacturacion}
          startIcon={loading && <CircularProgress size={20} color="inherit" />}
        >
          {loading ? 'Subiendo...' : 'Subir Factura'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceUploadModal;
